import * as ModelEvent from "@node-elion/syncron";

import SubscriptionPool from "../../redux/services/SubscriptionPool";
import createLogger from "../../utils/logger.util";
import IResponseWithItems from "../../types/IResponse";
import ServiceSubscribeOptionsBase from "../../types/ServiceSubscribeOptionsBase";
import Subscription from "../../types/Subscription";
import { NonEditableProperties } from "../../types/NonEditableProperties";
import {
	Base,
	TaxiServiceOld as TaxiService,
	Executor,
	Client,
	Dispatcher,
	Language,
} from "..";

const logger = createLogger({ name: "BankTransaction" });

class BankTransaction extends Base {
	public static PaymentTransactionStatus = {
		PENDING: "pending",
		COMPLETED: "completed",
		CANCELLED: "cancelled",
	} as const;

	public static BankTransactionType = {
		ORDER_PAYMENT: "order_payment",
		REPLENISHMENT_EXECUTOR_CARD: "replenishment_executor_card",
		REPLENISHMENT_CUSTOMER_CARD: "replenishment_customer_card",
	} as const;

	public static RemountTransactionType = {
		HOLD: "hold",
		REFUND: "refund",
		WITHDRAW: "withdraw",
		REPLENISHMENT: "replenishment",
		UNKNOWN: "unknown",
		AUTH_REFUND: "auth_refund",
		AUTH: "auth",
		CAPTURE: "capture",
	} as const;

	public static StatusDescription = {
		START_REGISTRATION_CUSTOMER_CARD: "start registration customer card",
		START_REGISTRATION_EXECUTOR_CARD: "start registration executor card",
		REFUND_REGISTRATION_AMOUNT_TO_CUSTOMER_CARD:
			"refund registration amount to customer card",
		REFUND_REGISTRATION_AMOUNT_TO_EXECUTOR_CARD:
			"refund registration amount to executor card",
		REFUND_REGISTRATION_FAILED: "refund registration failed",
		START_REFUND_REGISTRATION_CUSTOMER_CARD:
			"start refund registration customer card",
		REGISTRATION_CUSTOMER_CARD_SUCCESS:
			"registration customer card success",
		REGISTRATION_EXECUTOR_CARD: "registration executor card",
		REGISTRATION_EXECUTOR_CARD_SUCCESS:
			"registration executor card success",
		ERROR_REGISTRATION_AMOUNT_TO_CUSTOMER_CARD:
			"error registration amount to customer card",
		ERROR_REGISTRATION_AMOUNT_TO_EXECUTOR_CARD:
			"error registration amount to executor card",
		START_WITHDRAW_CUSTOMER_CARD: "start withdraw customer card",
		START_WITHDRAW_EXECUTOR_CARD: "start withdraw executor card",
		WITHDRAW_INITIATED_WAITING_CALLBACK:
			"withdraw initiated, waiting callback",
		PAYMENT_SUCCESS: "payment success",
		PAYMENT_DECLINED: "payment declined",
		PAYMENT_REFUNDED: "payment refunded",
		WITHDRAW_EXECUTOR_CARD_SUCCESS: "withdraw executor card success",
		ERROR_WITHDRAW_EXECUTOR_CARD: "error withdraw executor card",
		SYSTEM_FEE_MORE_THAN_WITHDRAW_AMOUNT:
			"system fee more then withdraw amount",
		START_HOLD_CUSTOMER_CARD: "start hold customer card",
		HOLD_INITIATED_WAITING_CALLBACK: "hold initiated, waiting callback",
		HOLD_APPROVED: "hold approved",
		HOLD_DECLINED: "hold declined",
		HOLD_FAILED: "Hold failed",
		HOLD_REFUNDED: "hold refunded",
		WARNING_HOLD_WAS_REFUNDED: "Warning! hold was refunded",
		START_CONFIRM_HOLD_CUSTOMER_CARD: "start confirm hold customer card",
		HOLD_CONFIRMED: "Hold confirmed",
		HOLD_CONFIRMATION_FAILED: "Hold confirmation failed",
		HOLD_CONFIRMATION_ERROR: "Hold confirmation error",
		START_REFUND_HOLD_CUSTOMER_CARD: "start refund hold customer card",
		REFUND_HOLD_REMAINDER: "refund hold remainder",
		REFUND_PAYMENT_CUSTOMER_CARD: "refund payment customer card",
		REFUND_FAILED: "Refund failed",
		REFUND_SUCCESS: "refund success",
		REFUND_ERROR: "Refund error",
		NO_SAVED_CARD_TOKEN_FOUND: "No saved card token found",
		PAYMENT_SESSION_TIMED_OUT: "Your payment session has timed out",
		FAILED_TO_MAKE_PAYMENT:
			"Failed to make payment. Please make sure the parameters are entered correctly and try again",
		LIMIT_IS_EXCEEDED: "Limit is exceeded",
		TRANSACTION_EXPIRED: "transaction expired",
		VALUE_CANNOT_BE_NULL_TRANSACTION_STATE:
			"Value cannot be null. Parameter name: TransactionState",
		VALUE_CANNOT_BE_NULL_EMPTY_REQUEST:
			"Value cannot be null. Parameter name: Empty request",
		INSUFFICIENT_FUNDS: "Insufficient funds",
		AUTHENTICATION_FAILURE: "Authentication failure",
	} as const;

	public static fromResponse(data: any): BankTransaction.Model {
		return {
			id: data.id,
			transactionId: data.transactionId,
			executor: data.executor
				? Executor.fromResponse(data.executor) || null
				: null,
			customer: data.customer ? Client.fromResponse(data.customer) : null,
			dispatcher: data.dispatcher
				? Dispatcher.fromResponse(data.dispatcher)
				: null,
			taxiService: data.taxiService
				? TaxiService.fromResponse(data.taxiService)
				: null,
			order: data.order,
			amount: Number(data.amount) || 0,
			amountWithFee: Number(data.amountWithFee) || 0,
			callbackRaw: data.callbackRaw,
			paymentTransaction: data.paymentTransaction,
			paymentSystemAccount: data.paymentSystemAccount,
			status: data.status,
			type: data.type,

			amountFromCustomer: Number(data.amountFromCustomer) || 0,
			amountToExecutorCard: Number(data.amountToExecutorCard) || 0,
			amountToExecutorMain: Number(data.amountToExecutorMain) || 0,
			statusDescription: data.statusDescription || "",
			createdAt: data.createdAt,
			updatedAt: data.updatedAt,
			deletedAt: data.deletedAt,
			remountTransactionType:
				data.remountTransactionType ||
				this.RemountTransactionType.UNKNOWN,
			orderNumber: data.order?.orderNumber,
			orderId: data.order?.id,
			taxiServiceId: data.taxiServiceId ?? data.taxiService?.id ?? null,
			tripReport: data.tripReport || null,
		};
	}

	public static async getAll(
		object?: BankTransaction.SubscribeOptions,
	): Promise<IResponseWithItems<BankTransaction.Model>> {
		const response = await this.request((prpc) =>
			prpc.theirsModel.bankTransaction.getAll(object),
		);

		const models = response?.items?.map(this.fromResponse);
		logger.info("[BankTransaction] getAll", object, models);

		return {
			...response,
			items: models,
		};
	}

	public static async subscribe(
		options: BankTransaction.SubscribeOptions,
		onUpdate: Subscription.OnUpdate<BankTransaction.Model>,
	): Promise<Subscription<BankTransaction.SubscribeOptions> | null> {
		const modelEventConstructor = new ModelEvent.ModelEventConstructor({
			onUpdate: (state) => {
				const models = state.models.map(this.fromResponse);
				onUpdate({
					...state,

					models,
				});

				logger.info("[BankTransaction] subscribe", options, models);
			},
		});

		const subscription = await SubscriptionPool.add(
			(prpc) =>
				prpc.theirsModel.bankTransaction.subscribe({
					params: this.optionsToRequest(options),
					ping: () => true,
					onEvent: (events) => {
						modelEventConstructor.onEvent(events);
					},
					onError: (error) => {
						logger.error(error);
					},
				}),
			{ name: "BankTransaction.subscribe" },
		);

		return {
			unsubscribe: () => subscription.unsubscribe(),
			update: (options: BankTransaction.SubscribeOptions) =>
				subscription.update(this.optionsToRequest(options)),
		} as Subscription<BankTransaction.SubscribeOptions>;
	}

	private static optionsToRequest(options: BankTransaction.SubscribeOptions) {
		return {
			limit: options.limit,
			offset: options.offset,
			query: options.query,
			statuses: options.statuses,
			types: options.types,
			excludeTypes: options.excludeTypes,
			orderIds: options.orderIds,
			dateRange: options.dateRange,
			lang: options.language,
			order: options.order,
		};
	}
}

declare namespace BankTransaction {
	interface Model extends NonEditableProperties {
		id: number;
		transactionId: number;
		amount: number;
		amountWithFee: number;
		dispatcher: Dispatcher.Model | null;
		customer: Client.Model | null;
		executor: Executor.Model | null;
		taxiService: TaxiService.Model | null;
		order: any;
		callbackRaw: CallbackRaw;
		paymentTransaction: null;
		paymentSystemAccount: any;
		status: PaymentTransactionStatus;
		type: BankTransactionType;
		remountTransactionType: BankTransaction.RemountTransactionType;
		orderNumber: number | null;
		orderId: number;
		amountFromCustomer: number;
		amountToExecutorCard: number;
		amountToExecutorMain: number;
		statusDescription: BankTransaction.StatusDescription;
		taxiServiceId?: number | null;
		/** Снапшот отчета о поездке (банковский чек Victoriabank). */
		tripReport?: TripReport | null;
	}

	/**
	 * Данные отчета о поездке, собранные бэкендом при успешной оплате
	 * (Cybersource / Victoriabank).
	 */
	interface TripReport {
		merchantName?: string;
		merchantCountry?: string;
		merchantWebsite?: string;
		transactionDate?: string;
		amount?: number;
		currency?: string;
		orderNumber?: string;
		rrn?: string;
		authorizationCode?: string;
		cardType?: string;
		cardSuffix?: string;
		customerName?: string;
		serviceDescription?: string;
		refundPolicyUrl?: string;
		supportContacts?: string;
		operations?: TripReportOperation[];
	}

	type TripReportOperationType =
		| "hold"
		| "capture"
		| "withdraw"
		| "refund";

	interface TripReportOperation {
		type: TripReportOperationType;
		transactionDate?: string;
		amount?: number;
		currency?: string;
		rrn?: string;
		authorizationCode?: string;
	}

	type RemountTransactionType =
		(typeof BankTransaction.RemountTransactionType)[keyof typeof BankTransaction.RemountTransactionType];

	type StatusDescription =
		(typeof BankTransaction.StatusDescription)[keyof typeof BankTransaction.StatusDescription];

	type PaymentTransactionStatus =
		(typeof BankTransaction.PaymentTransactionStatus)[keyof typeof BankTransaction.PaymentTransactionStatus];

	type BankTransactionType =
		(typeof BankTransaction.BankTransactionType)[keyof typeof BankTransaction.BankTransactionType];

	interface SubscribeOptions
		extends ServiceSubscribeOptionsBase<BankTransaction.Model> {
		types?: BankTransactionType[];
		/** Types excluded from the result (server-side filter). */
		excludeTypes?: BankTransactionType[];
		statuses?: PaymentTransactionStatus[];
		orderIds?: number[];
		dateRange?: {
			from: Date;
			to: Date;
		};
		language?: Language;
	}

	interface CallbackRaw {
		Card: {
			CardBin: string | null;
			CardBank: string | null;
			CardMask: string | null;
			CardType: string | null;
			CardBrand: string | null;
			CardLevel: string | null;
			NameOnCard: string | null;
			CardCountry: string | null;
			PaymentToken: string | null;
		};
		Order: {
			Fee: number;
			Amount: number;
			OrderId: string;
			Currency: string | null;
			AltAmount: number;
			CustomInfo: string | null;
			AltCurrency: string | null;
			CreditAmount: number;
		};
		Address: {
			Zip: string | null;
			City: string | null;
			Zone: string | null;
			Country: string | null;
			FullAddress: string | null;
		};
		Payment: {
			Eci: string | null;
			Rrn: string | null;
			Status: string | null;
			ErrorCode: number;
			InvoiceId: string | null;
			Signature: string;
			StatusCode: number;
			TerminalId: string;
			BankOrderId: string | null;
			IsRedirect3DS: boolean;
			OperationType: string;
			TransactionId: number;
			CapturedAmount: number;
			RefundedAmount: number;
			PaymentSystemId: number;
			TransactionDate: string;
			ErrorDescription: string | null;
			OperationTypeCode: number;
		};
		Customer: {
			Email: string | null;
			Phone: string | null;
			LastName: string | null;
			FirstName: string | null;
			CustomerId: string | null;
		};
		Products: null;
		IpAddress: string;
		Recurrent: null;
		RefundedProducts: null;
	}
}

export default BankTransaction;
